/* eslint-disable max-len */

import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { actions } from 'modules/user';
import ChecklistItem from './components/ChecklistItem';
import ReactMarkdown from 'lib/react-markdown';
import { Col, Row } from 'reactstrap';
import track from '../../../../lib/track';

const Checklist = forwardRef((props, ref) => {
  const {
    data,
    id,
    get,
    post,
    test,
    overrideTitle,
    readonly,
    HeaderWrapperTag = React.Fragment,
    handleComplete,
    userAnalytics,
    updateModuleStates,
    parentID
  } = props;

  const { checklistItems = [], description } = data || {};

  // Initialize state
  const getInitialState = () => {
    const initialValues = {};
    checklistItems.forEach(checklistItem => {
      initialValues[checklistItem.name] = false;
    });

    return {
      start: {
        timeCompleted: null,
        values: initialValues
      },
      end: {
        timeCompleted: null,
        values: initialValues
      }
    };
  };

  const [state, setState] = useState(getInitialState);

  // Expose methods via ref for parent components
  useImperativeHandle(ref, () => ({
    markCompleteByIndex: idx => {
      if (checklistItems[idx]) {
        updateValue(checklistItems[idx].name);
      }
    }
  }));

  const isStartOrEnd = useCallback(() => {
    return !state.start.timeCompleted ? 'start' : 'end';
  }, [state.start.timeCompleted]);

  const getChecklistResponses = useCallback(async () => {
    const savedData = await get(id);

    if (savedData) {
      setState({ ...savedData });
      // Optional - only if parent needs updating on complete, ie onboard
      if (savedData.complete && handleComplete) {
        handleComplete(id);
      }
    }
  }, [get, id, handleComplete]);

  useEffect(() => {
    if (!test) {
      getChecklistResponses();
    }
  }, [test, getChecklistResponses]);

  const updateValue = useCallback(
    (name, e) => {
      if (e) {
        e.preventDefault();
      }

      track('Checklist', userAnalytics ? { ...userAnalytics, ux: 'checked' } : { service: 'default', ux: 'checked' });

      if (e && e.target && e.target.href) {
        window.location = e.target.href;
      } else {
        const currentPhase = isStartOrEnd();
        const newState = {
          ...state,
          [currentPhase]: {
            ...state[currentPhase],
            values: {
              ...state[currentPhase].values,
              [name]: !state[currentPhase].values[name]
            }
          }
        };

        // Optional - if this is an onboard
        if (typeof handleComplete === 'function') {
          const fields = newState.start.values;
          const isComplete = Object.values(fields).every(value => value);

          if (isComplete) {
            handleComplete(id);
            newState.complete = true;
            track(
              'Checklist',
              userAnalytics ? { ...userAnalytics, ux: 'completed' } : { service: 'default', ux: 'completed' }
            );
          }
        }

        setState(newState);
        post(id, newState);

        // Update any parent wanting progress report on this checklist
        const checkboxProgress = Object.values(newState.start.values);
        const finished = checkboxProgress.filter(item => item);

        if (updateModuleStates && parentID) {
          updateModuleStates(parentID, null, {
            id,
            numCompleted: finished.length,
            numPossible: checkboxProgress.length
          });
        }
      }
    },
    [state, isStartOrEnd, userAnalytics, handleComplete, id, post, updateModuleStates, parentID]
  );

  const { values } = state[isStartOrEnd()];
  const checkboxProgress = Object.values(values);
  const finished = checkboxProgress.filter(item => item);

  return (
    <>
      <HeaderWrapperTag>
        <Row className="align-items-center px-0">
          <Col className="px-0">
            <h4>{overrideTitle || 'Checklist'}</h4>
            {description ? <ReactMarkdown>{description}</ReactMarkdown> : null}
          </Col>
          <Col xs="12" md="auto" className="px-0">
            {finished.length}/{checkboxProgress.length} completed
          </Col>
        </Row>
      </HeaderWrapperTag>
      {checklistItems.map((checklistItem, index) => (
        <ChecklistItem
          key={checklistItem.name}
          isLast={index === checklistItems.length - 1}
          value={values[checklistItem.name] || false}
          change={!readonly ? updateValue : () => null}
          {...checklistItem}
        />
      ))}
    </>
  );
});

Checklist.displayName = 'Checklist';

Checklist.propTypes = {
  data: PropTypes.shape({
    checklistItems: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired
      })
    )
  }),
  get: PropTypes.func.isRequired,
  id: PropTypes.number,
  post: PropTypes.func.isRequired,
  test: PropTypes.bool,
  overrideTitle: PropTypes.string,
  readonly: PropTypes.bool,
  HeaderWrapperTag: PropTypes.elementType,
  handleComplete: PropTypes.func,
  userAnalytics: PropTypes.object,
  updateModuleStates: PropTypes.func,
  parentID: PropTypes.number
};

Checklist.defaultProps = {
  data: { checklistItems: [] },
  id: 88888888,
  test: false,
  overrideTitle: null,
  readonly: false,
  HeaderWrapperTag: React.Fragment,
  handleComplete: null,
  userAnalytics: null,
  updateModuleStates: null,
  parentID: null
};

const mapDispatchToProps = dispatch => ({
  get: contentId => dispatch(actions.getUserData(contentId)),
  post: (contentId, data) => dispatch(actions.postUserData(contentId, data))
});

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(Checklist);
